/*
Hero: one load sequence. Nothing in the hero reacts to the scroll position.
Why: the hero sets the hierarchy in the first second and at no other time.

More than one slide gives a cross fade. Each photograph fades in over the one
before it, which stays put underneath. The photographs fade through the set one
time. The sequence then stops on the last frame. It does not go back to the
first frame. MOTION.md bans an infinite loop. This is not an infinite loop.
One slide starts no sequence.
*/

const HOLD = 4.4;
const FADE = 1.5;

/*
The opening frame holds longer than the rest. It is the frame a visitor reads
the headline against, and it is the one frame that carries colour, so it is
worth the seconds. Every later frame gets HOLD.
*/
const OPENING = 7.5;

/*
Move the held back sources of one slide to the real attributes.

Each slide after the first keeps its src and its srcset in data attributes. The
template does this. See sfaa_hero_slide_markup() in inc/media.php. Without it
the browser gets every photograph at load time, because the slides are all
inside the window and opacity does not stop a lazy image.
*/
function load(slide) {
  if (!slide) return;

  slide.querySelectorAll('[data-src], [data-srcset]').forEach((el) => {
    if (el.dataset.srcset) el.srcset = el.dataset.srcset;
    if (el.dataset.src) el.src = el.dataset.src;

    el.removeAttribute('data-srcset');
    el.removeAttribute('data-src');
  });
}

/* Cross fade through the remaining slides once, then stop on the last. */
function sequence(gsap, slides) {
  const tl = gsap.timeline({ delay: OPENING, defaults: { ease: 'power2.inOut' } });

  /* Get the second photograph now. The delay gives it OPENING seconds. */
  load(slides[1]);

  /*
  Only the incoming slide moves. The outgoing one stays at full opacity
  underneath and is covered as the new one arrives.

  Fading both at once looks correct and is not. Two stacked layers at half
  opacity sum to 1 but cover only 0.75 of the frame, because the second half
  of the top layer reveals the half transparent layer under it, and the figure
  background shows through the rest. Measured at the midpoint: opacities 0.472
  and 0.527, coverage 0.751, so a quarter of the frame went to the surface
  colour and the dissolve dipped dark in the middle. The old heavy grade hid
  it. The lighter one does not.

  Slides are absolutely positioned siblings, so slide N+1 already paints over
  slide N. Nothing has to be taken away for the new frame to be seen.
  */
  for (let i = 1; i < slides.length; i += 1) {
    tl.to(slides[i], { opacity: 1, duration: FADE })
      .call(load, [slides[i + 1]])
      .to({}, { duration: HOLD });
  }

  /*
  Bring the wash back over the first fade. Added after the loop so the loop
  keeps appending from zero. See --hero-wash in src/css/home/hero.css.
  */
  tl.to(slides[0].parentElement, { '--hero-wash': 1, duration: FADE }, 0);
}

export function hero(gsap) {
  const slides = document.querySelectorAll('.hero__slide');
  const rising = document.querySelectorAll('.hero h1, .hero__copy p, .hero__actions');

  if (!rising.length) return;

  const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

  /* Scale the photograph itself, not the wrapper, which has no size of its own. */
  const first = slides.length ? slides[0].querySelector('img') : null;

  if (first) {
    tl.fromTo(first, { scale: 1.12, opacity: 0 }, { scale: 1, opacity: 1, duration: 1.9 }, 0);
  }

  tl.fromTo(rising, { y: 46, opacity: 0 }, {
    y: 0,
    opacity: 1,
    duration: 1.25,
    stagger: 0.11,
    clearProps: 'transform',
  }, 0.35);

  if (slides.length > 1) sequence(gsap, slides);
}
